import React, { useState, useEffect } from "react";
import {
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Chip,
  IconButton,
  Grid,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  TrendingUp,
  TrendingDown,
} from "@mui/icons-material";
import TF from "../api/requests/tf_backend";

const PlaceOrder = () => {
  const params = new URLSearchParams(location.search);
  const symbol = params.get("symbol");

  const [stockDetails, setStockDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orderSuccess, setOrderSuccess] = useState(null);

  const [mainOrder, setMainOrder] = useState({
    quantity: "",
    amount: "",
  });

  const [gttOrders, setGttOrders] = useState([
    { price: "", amount: "", quantity: "" },
  ]);

  // Fetch stock data
  useEffect(() => {
    const fetchStockData = async () => {
      try {
        const stockData = await TF.fetchStockDetails({ symbol, duration: 30 });
        setStockDetails(stockData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching stock details:", error);
        setLoading(false);
      }
    };

    if (symbol) {
      fetchStockData();
    }
  }, [symbol]);

  const handleMainOrderChange = (field, value) => {
    const numValue = value.replace(/[^0-9]/g, "");
    const lastPrice = stockDetails?.quoteInfo?.[`NSE:${symbol}`]?.last_price || 0;

    setMainOrder((prev) => {
      const newDetails = { ...prev };
      if (field === "quantity") {
        newDetails.quantity = numValue;
        newDetails.amount = (numValue * lastPrice).toFixed(2);
      } else {
        newDetails.amount = numValue;
        newDetails.quantity = Math.floor(numValue / lastPrice);
      }
      return newDetails;
    });
  };

  const handleGttChange = (index, field, value) => {
    const numValue = value.replace(/[^0-9]/g, "");
    setGttOrders((prev) => {
      const newOrders = [...prev];
      const order = { ...newOrders[index] };

      if (field === "quantity") {
        order.quantity = numValue;
        order.amount = (numValue * order.price).toFixed(2);
      } else if (field === "amount") {
        order.amount = numValue;
        order.quantity = Math.floor(numValue / order.price);
      } else {
        order.price = numValue;
        if (order.quantity) {
          order.amount = (order.quantity * numValue).toFixed(2);
        }
      }

      newOrders[index] = order;
      return newOrders;
    });
  };

  const deleteGttOrder = (index) => {
    setGttOrders((prev) => prev.filter((_, i) => i !== index));
  };

  // Place regular order
  const handlePlaceOrder = async () => {
    try {
      const response = await TF.placeOrder({
        tradingsymbol: symbol,
        exchange: "NSE",
        transaction_type: "BUY",
        quantity: parseInt(mainOrder.quantity),
        order_type: "MARKET",
        product: "CNC",
        variety: "regular",
      });

      setOrderSuccess({
        type: "success",
        message: "Order placed successfully!",
      });
    } catch (error) {
      setOrderSuccess({
        type: "error",
        message: error?.response?.data || "Failed to place order.",
      });
    }
  };

  // Place GTT orders
  const handlePlaceGttOrders = async () => {
    try {
      const promises = gttOrders.map((order) =>
        TF.placeOrder({
          tradingsymbol: symbol,
          exchange: "NSE",
          transaction_type: "BUY",
          quantity: parseInt(order.quantity),
          order_type: "LIMIT",
          price: parseFloat(order.price),
          product: "CNC",
          variety: "regular",
        })
      );

      await Promise.all(promises);
      setOrderSuccess({
        type: "success",
        message: "GTT orders placed successfully!",
      });
    } catch (error) {
      setOrderSuccess({
        type: "error",
        message: error?.response?.data || "Failed to place GTT orders.",
      });
    }
  };

  if (loading) return <Typography>Loading...</Typography>;
  if (!stockDetails) return <Typography>No data available</Typography>;

  const currentStock = stockDetails?.quoteInfo?.[`NSE:${symbol}`];
  const percentageChange = stockDetails.percentageChange?.toFixed(2);

  return (
    <Box sx={{ p: 3, maxWidth: "1200px", margin: "0 auto" }}>
      {orderSuccess && (
        <Alert
          severity={orderSuccess.type}
          sx={{ mb: 2 }}
          onClose={() => setOrderSuccess(null)}
        >
          {orderSuccess.message}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Market Overview */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Typography sx={{ fontSize: "1.125rem", fontWeight: 500 }}>
                {symbol}
              </Typography>
              <Chip
                label="NSE"
                size="small"
                sx={{ bgcolor: "rgba(25, 118, 210, 0.1)", height: 24 }}
              />
              <Typography sx={{ fontSize: "1.125rem", fontWeight: 500 }}>
                ₹{currentStock?.last_price}
              </Typography>
              <Chip
                icon={percentageChange >= 0 ? <TrendingUp /> : <TrendingDown />}
                label={`${percentageChange}%`}
                color={percentageChange >= 0 ? "success" : "error"}
                size="small"
                sx={{ height: 24 }}
              />
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography sx={{ fontSize: "0.875rem", color: "#666" }}>
                  Previous Close
                </Typography>
                <Typography sx={{ fontSize: "0.875rem" }}>
                  ₹{currentStock?.ohlc?.close}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography sx={{ fontSize: "0.875rem", color: "#666" }}>
                  Day's Range
                </Typography>
                <Typography sx={{ fontSize: "0.875rem" }}>
                  ₹{currentStock?.ohlc?.low} - ₹{currentStock?.ohlc?.high}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography sx={{ fontSize: "0.875rem", color: "#666" }}>
                  Circuit Limits
                </Typography>
                <Typography sx={{ fontSize: "0.875rem" }}>
                  ₹{currentStock?.lower_circuit_limit} - ₹
                  {currentStock?.upper_circuit_limit}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Order Form */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            {/* Main Order */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, color: "#666", fontSize: "0.875rem" }}
              >
                Market Order
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  size="small"
                  placeholder="Quantity"
                  value={mainOrder.quantity}
                  onChange={(e) => handleMainOrderChange("quantity", e.target.value)}
                  sx={{ flex: 1 }}
                  InputProps={{ sx: { fontSize: "0.875rem" } }}
                />
                <TextField
                  size="small"
                  placeholder="Amount"
                  value={mainOrder.amount}
                  onChange={(e) => handleMainOrderChange("amount", e.target.value)}
                  sx={{ flex: 1 }}
                  InputProps={{ sx: { fontSize: "0.875rem" } }}
                />
              </Box>
              <Button
                variant="contained"
                fullWidth
                onClick={handlePlaceOrder}
                sx={{
                  mt: 1,
                  bgcolor: "#1976d2",
                  fontSize: "0.875rem",
                  "&:hover": { bgcolor: "#1565c0" },
                }}
              >
                Place Market Order
              </Button>
            </Box>

            {/* GTT Orders */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: "#666", fontSize: "0.875rem" }}
                >
                  GTT Orders
                </Typography>
                <IconButton
                  onClick={() =>
                    setGttOrders((prev) => [
                      ...prev,
                      { price: "", amount: "", quantity: "" },
                    ])
                  }
                  size="small"
                  sx={{ ml: 1 }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>

              {gttOrders.map((order, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Price"
                      value={order.price}
                      onChange={(e) => handleGttChange(index, "price", e.target.value)}
                      sx={{ flex: 1 }}
                      InputProps={{ sx: { fontSize: "0.875rem" } }}
                    />
                    <TextField
                      size="small"
                      placeholder="Quantity"
                      value={order.quantity}
                      onChange={(e) =>
                        handleGttChange(index, "quantity", e.target.value)
                      }
                      sx={{ flex: 1 }}
                      InputProps={{ sx: { fontSize: "0.875rem" } }}
                    />
                    <TextField
                      size="small"
                      placeholder="Amount"
                      value={order.amount}
                      onChange={(e) => handleGttChange(index, "amount", e.target.value)}
                      sx={{ flex: 1 }}
                      InputProps={{ sx: { fontSize: "0.875rem" } }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => deleteGttOrder(index)}
                      sx={{ color: "#d32f2f" }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))}

              {gttOrders.length > 0 && (
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handlePlaceGttOrders}
                  sx={{
                    fontSize: "0.875rem",
                    color: "#1976d2",
                    borderColor: "#1976d2",
                    "&:hover": {
                      borderColor: "#1565c0",
                      bgcolor: "rgba(25, 118, 210, 0.04)",
                    },
                  }}
                >
                  Place GTT Orders
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PlaceOrder;